// ----------------------------------------------------------------------

// Shared sx objects for brandbook font roles — import these instead of hardcoding fontFamily strings
export const monoSx = {
  fontFamily: 'var(--font-mono), "JetBrains Mono", "SF Mono", Consolas, monospace',
  fontSize: '0.75rem',
};

export const displaySx = {
  fontFamily: 'var(--font-display)',
};

// ----------------------------------------------------------------------

export const stylesMode = {
  light: '[data-mui-color-scheme="light"] &',
  dark: '[data-mui-color-scheme="dark"] &',
};

export const mediaQueries = {
  upXs: '@media (min-width:0px)',
  upSm: '@media (min-width:600px)',
  upMd: '@media (min-width:900px)',
  upLg: '@media (min-width:1200px)',
  upXl: '@media (min-width:1536px)',
};

/**
 * Set font family
 */
export function setFont(fontName) {
  const resolvedName = fontName && typeof fontName === 'string' ? fontName : 'var(--font-onest)';
  const quoted = resolvedName.startsWith('var(') ? resolvedName : `"${resolvedName}"`;
  return `${quoted},-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"`;
}

/**
 * Converts rem to px
 */
export function remToPx(value) {
  return Math.round(parseFloat(value) * 16);
}

/**
 * Converts px to rem
 */
export function pxToRem(value) {
  return `${value / 16}rem`;
}

/**
 * Responsive font sizes
 */
export function responsiveFontSizes({ sm, md, lg }) {
  return {
    [mediaQueries.upSm]: { fontSize: pxToRem(sm) },
    [mediaQueries.upMd]: { fontSize: pxToRem(md) },
    [mediaQueries.upLg]: { fontSize: pxToRem(lg) },
  };
}

/**
 * Converts a hex color to RGB channels
 */
// export function hexToRgbChannel(hex) {
//   if (!/^#[0-9A-F]{6}$/i.test(hex)) {
//     throw new Error(`Invalid hex color: ${hex}`);
//   }

//   const r = parseInt(hex.substring(1, 3), 16);
//   const g = parseInt(hex.substring(3, 5), 16);
//   const b = parseInt(hex.substring(5, 7), 16);

//   return `${r} ${g} ${b}`;
// }
export const hexToRgbChannel = (hex) => {
  // Check if hex is undefined or null
  if (!hex) {
    return '0 0 0'; // Return a default value
  }

  // Remove # if present
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;

  const match = cleanHex.match(/\w\w/g);
  if (!match || match.length !== 3) {
    // Return fallback value instead of throwing an error
    return '0 0 0'; // Black as fallback
  }

  const [r, g, b] = match.map((x) => parseInt(x, 16));
  return `${r} ${g} ${b}`; // Converts "#919EAB" to "145 158 171"
};

/**
 * Converts a hex color to RGB channels
 */
export function createPaletteChannel(hexPalette) {
  const channelPalette = {};

  Object.entries(hexPalette).forEach(([key, value]) => {
    channelPalette[`${key}Channel`] = hexToRgbChannel(value);
  });

  return { ...hexPalette, ...channelPalette };
}

/**
 * Color with alpha channel
 */
// export function varAlpha(color, opacity = 1) {
//   const unsupported =
//     color.startsWith('#') ||
//     color.startsWith('rgb') ||
//     color.startsWith('rgba') ||
//     (!color.includes('var') && color.includes('Channel'));

//   if (unsupported) {
//     throw new Error(`[Alpha]: Unsupported color format "${color}".
//      Supported formats are:
//      - RGB channels: "0 184 217".
//      - CSS variables with "Channel" prefix: "var(--palette-common-blackChannel, #000000)".
//      Unsupported formats are:
//      - Hex: "#00B8D9".
//      - RGB: "rgb(0, 184, 217)".
//      - RGBA: "rgba(0, 184, 217, 1)".
//      `);
//   }

//   return `rgba(${color} / ${opacity})`;
// }
export function varAlpha(color, opacity = 1) {
  // Check if color is undefined or null
  if (!color) {
    return `rgba(0, 0, 0, ${opacity})`;
  }

  // Check if the color format is not directly supported
  const unsupported =
    color.startsWith('#') ||
    color.startsWith('rgb') ||
    color.startsWith('rgba') ||
    (!color.includes('var') && color.includes('Channel'));

  if (unsupported) {
    // Handle unsupported formats instead of throwing an error
    if (color.startsWith('#')) {
      // Convert hex to RGB channel format
      const cleanHex = color.startsWith('#') ? color.slice(1) : color;
      const match = cleanHex.match(/\w\w/g);
      if (match && match.length === 3) {
        const [r, g, b] = match.map((x) => parseInt(x, 16));
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
    } else if (color.startsWith('rgb(')) {
      // Extract values from rgb format and add alpha
      const rgbValues = color.match(/\d+/g);
      if (rgbValues && rgbValues.length === 3) {
        return `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${opacity})`;
      }
    } else if (color.startsWith('rgba(')) {
      // For rgba, just replace the existing alpha
      const rgbaValues = color.match(/\d+/g);
      if (rgbaValues && rgbaValues.length >= 3) {
        return `rgba(${rgbaValues[0]}, ${rgbaValues[1]}, ${rgbaValues[2]}, ${opacity})`;
      }
    }

    // Fallback if conversion fails
    return `rgba(0, 0, 0, ${opacity})`;
  }

  // Original functionality for supported formats
  return `rgba(${color} / ${opacity})`;
}
